/**
 * O botão "Sincronizar modelos" — o mesmo do painel do motor de conversa.
 *
 * ── Por que existe, se a lista de modelos já vem da Meta ──────────────────
 *
 * A lista desta tela é lida da Meta a cada abertura, então ela já está certa.
 * O que atrasava era o ATENDIMENTO: ele oferece o catálogo que o CliqCRM relê
 * do motor de 15 em 15 minutos, e o motor relê a Meta de 3 em 3 horas. Um
 * modelo aprovado agora podia levar meia hora para aparecer na conversa, com
 * esta tela dizendo "Aprovado" o tempo todo.
 *
 * ── O recado diz o que aconteceu, e não "pronto" ──────────────────────────
 *
 * O motor sincroniza em segundo plano e não avisa quando termina; a API espera
 * alguns segundos pela mudança. Por isso há dois recados: "atualizados" quando
 * o catálogo mudou, e "nada mudou até agora" quando não — que é o caso de não
 * haver modelo novo e também o de a Meta ter demorado. Afirmar "sincronizado"
 * nos dois seria dizer mais do que se sabe.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CanalDeWhatsApp } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao } from '@/shared/ui';
import './sincronizar-modelos.css';

function disponiveis(quantos: number): string {
  if (quantos === 0) return 'Nenhum modelo aprovado disponível no Atendimento.';
  if (quantos === 1) return '1 modelo aprovado disponível no Atendimento.';
  return `${quantos} modelos aprovados disponíveis no Atendimento.`;
}

export function SincronizarModelos({ canal }: { canal: CanalDeWhatsApp }): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [recado, definirRecado] = useState<{ ok: boolean; texto: string } | null>(null);

  const sincronizar = useMutation({
    mutationFn: () =>
      api!.sincronizarModelosDeMensagem({ caminho: { id: canal.referencia_externa } }),
    onMutate: () => definirRecado(null),
    onSuccess: async (resultado) => {
      await Promise.all([
        cache.invalidateQueries({ queryKey: chaves.modelosDeMensagem(canal.referencia_externa) }),
        /*
         * As conversas abertas trazem o catálogo nas capacidades delas. Sem
         * isto, quem estivesse com uma conversa aberta no Atendimento só veria
         * o modelo novo depois de trocar de conversa.
         */
        cache.invalidateQueries({ queryKey: ['conversa'] }),
      ]);
      definirRecado({
        ok: true,
        texto: resultado.mudou
          ? `Modelos atualizados. ${disponiveis(resultado.modelos)}`
          : `Nada mudou até agora. ${disponiveis(resultado.modelos)} Se um modelo foi ` +
            'aprovado há pouco, tente de novo em um minuto.',
      });
    },
    onError: (erro) =>
      definirRecado({
        ok: false,
        texto: recadoDeErro(erro, { generica: 'Não foi possível sincronizar os modelos agora.' }),
      }),
  });

  return (
    <div className="sincronizar-modelos">
      <Botao
        miudo
        desabilitado={sincronizar.isPending || api === null}
        motivo={api === null ? 'Sem conexão agora. Tente de novo em instantes.' : 'Sincronizando…'}
        title="Traz da Meta os modelos aprovados, para o Atendimento oferecer agora"
        onClick={() => sincronizar.mutate()}
      >
        {sincronizar.isPending ? 'Sincronizando…' : 'Sincronizar modelos'}
      </Botao>
      {recado && (
        <p
          className={recado.ok ? 'admin__nota admin__nota--ok' : 'admin__nota'}
          role={recado.ok ? 'status' : 'alert'}
        >
          {recado.texto}
        </p>
      )}
    </div>
  );
}
